import React, { useState } from 'react'
import IconButton from '@material-ui/core/IconButton'
import ExpandMoreOutlinedIcon from 'components/material-symbol/icons/ExpandMore'
import CoachUserAvatar from 'components/coach-user-avatar'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Divider from '@material-ui/core/Divider'
import Popover from '@material-ui/core/Popover'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import { makeStyles } from '@material-ui/core/styles'
import { Link as RouterLink } from 'react-router-dom'
import { useAuth } from 'lib/auth'
import AccountRouterLink from 'components/account-router-link'
import { useSupportContactActions } from 'lib/support'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { STRIPE_CUSTOMER_PORTAL_URL } from 'env'
import {
  MENU_DIVIDER_COLOR,
  MENU_INSET,
  MENU_ITEM_BORDER_RADIUS,
  MENU_ITEM_HOVER_BG,
  menuPaperStyles,
} from 'lib/styles/menu'

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(1),
    minWidth: 240,
    ...menuPaperStyles,
  },
  menuList: {
    padding: MENU_INSET,
    outline: 'none',
  },
  menuItem: {
    minHeight: 40,
    padding: theme.spacing(1, 1.5),
    borderRadius: MENU_ITEM_BORDER_RADIUS,
    gap: 6,
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: MENU_ITEM_HOVER_BG,
    },
    '&:hover $externalIcon, &.Mui-focusVisible $externalIcon': {
      color: theme.palette.text.secondary,
    },
  },
  externalIcon: {
    fontSize: 12,
    flexShrink: 0,
    color: theme.palette.text.disabled,
    transition: theme.transitions.create('color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  divider: {
    margin: `${MENU_INSET}px -${MENU_INSET}px`,
    backgroundColor: MENU_DIVIDER_COLOR,
  },
  mobileIdentity: {
    padding: `${12 + MENU_INSET}px ${12 + MENU_INSET}px 12px`,
    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  mobileIdentityName: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600,
  },
  mobileIdentityEmail: {
    fontSize: 12,
    lineHeight: '16px',
  },
  accountButton: {
    borderRadius: theme.shape.borderRadius,
  },
  userSummary: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    maxWidth: 240,
    marginRight: theme.spacing(1.5),
    textAlign: 'right',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  expandIconHiddenOnMobile: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  userSummaryText: {
    maxWidth: '100%',
    lineHeight: 1.3,
  },
}))

export default function AccountDropdown({ hideChevronOnMobile = false }) {
  const { user, isLoggedIn, isCoach, isConsumer } = useAuth()
  const { startSupportConversation } = useSupportContactActions()
  const classes = useStyles()
  const hasPaidTierSubscription = user?.coach?.hasPaidTierSubscription
  const isActiveMember =
    user?.coach?.pgaMembership?.membershipStatus === 'Active'

  const [anchorEl, setAnchorEl] = useState(null)

  const toggleAccountMenu = event => {
    setAnchorEl(el => (el ? null : event.currentTarget))
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  if (!isLoggedIn) return null

  return (
    <>
      <IconButton
        aria-label="account of current user"
        aria-controls="account-dropdown-menu"
        aria-haspopup="true"
        onClick={toggleAccountMenu}
        centerRipple={false}
        className={classes.accountButton}
      >
        {(user?.name || user?.email) && (
          <Box className={classes.userSummary}>
            {user?.name && (
              <Typography
                variant="body2"
                color="textPrimary"
                noWrap
                className={classes.userSummaryText}
              >
                {user.name}
              </Typography>
            )}
            {user?.email && (
              <Typography
                variant="caption"
                color="textSecondary"
                noWrap
                className={classes.userSummaryText}
              >
                {user.email}
              </Typography>
            )}
          </Box>
        )}
        <CoachUserAvatar
          style={{
            width: 40,
            height: 40,
          }}
        />
        <ExpandMoreOutlinedIcon
          fontSize="small"
          className={
            hideChevronOnMobile ? classes.expandIconHiddenOnMobile : undefined
          }
        />
      </IconButton>

      <Popover
        id="account-dropdown-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        elevation={0}
        PaperProps={{ className: classes.paper }}
        onClose={closeMenu}
      >
        {(user?.name || user?.email) && (
          <Box className={classes.mobileIdentity}>
            {user?.name && (
              <Typography
                color="textPrimary"
                noWrap
                className={classes.mobileIdentityName}
              >
                {user.name}
              </Typography>
            )}
            {user?.email && (
              <Typography
                color="textSecondary"
                noWrap
                className={classes.mobileIdentityEmail}
              >
                {user.email}
              </Typography>
            )}
          </Box>
        )}
        <MenuList autoFocusItem className={classes.menuList}>
          {isCoach ? (
            <>
              <AccountDropdownItem
                component={AccountRouterLink}
                to="/account"
                text="Account Details"
                onClick={closeMenu}
              />
              <SensitiveInfoWrapper>
                <AccountDropdownItem
                  component={AccountRouterLink}
                  to="/account/payments"
                  text="Payment Accounts"
                  onClick={closeMenu}
                />
              </SensitiveInfoWrapper>
              {hasPaidTierSubscription && (
                <SensitiveInfoWrapper>
                  <AccountDropdownItem
                    component="a"
                    target="_blank"
                    href={STRIPE_CUSTOMER_PORTAL_URL}
                    text="Manage Plan"
                    external
                    onClick={closeMenu}
                  />
                </SensitiveInfoWrapper>
              )}
              {isActiveMember && (
                <AccountDropdownItem
                  component={AccountRouterLink}
                  to="/account/sponsorships"
                  text="PGA Coach Sponsorships"
                  onClick={closeMenu}
                />
              )}
              {(user.familyCupSignedUp || user.jrlSignedUp) && (
                <AccountDropdownItem
                  component={AccountRouterLink}
                  to="/account/requirements"
                  text="Requirements"
                  onClick={closeMenu}
                />
              )}
              {user.isAcademyOwner && (
                <>
                  <Divider className={classes.divider} />
                  <AccountDropdownItem
                    component={AccountRouterLink}
                    to="/account/academy-coaches"
                    text="Manage Academy Coaches"
                    onClick={closeMenu}
                  />
                  <AccountDropdownItem
                    component={AccountRouterLink}
                    to="/account/bay-reservations"
                    text="Manage Bay Rentals"
                    onClick={closeMenu}
                  />
                  <AccountDropdownItem
                    component={AccountRouterLink}
                    to="/account/coaching-center"
                    text="Manage Academy Students"
                    onClick={closeMenu}
                  />
                </>
              )}
              {(window.location.href.includes('pga-coach') ||
                window.location.href.includes('junior-league')) && (
                <>
                  <Divider className={classes.divider} />
                  <AccountDropdownItem
                    text="Contact Us"
                    onClick={() => {
                      startSupportConversation('')
                      closeMenu()
                    }}
                  />
                  <AccountDropdownItem
                    component="a"
                    target="_blank"
                    href="https://support.pga.com/en/collections/2683103-pga-members"
                    text="PGA Coach Help Center"
                    external
                    onClick={closeMenu}
                  />
                </>
              )}
            </>
          ) : null}
          {isConsumer ? (
            <>
              <AccountDropdownItem
                component={AccountRouterLink}
                to="/account/payments"
                text="Transactions"
                onClick={closeMenu}
              />
              <AccountDropdownItem
                component={AccountRouterLink}
                to="/account/students"
                text="Accounts"
                onClick={closeMenu}
              />
            </>
          ) : null}
          <Divider className={classes.divider} />
          <AccountDropdownItem
            component={RouterLink}
            to="/logout"
            text="Logout"
            onClick={closeMenu}
          />
        </MenuList>
      </Popover>
    </>
  )
}

function AccountDropdownItem({ text, external = false, ...props }) {
  const classes = useStyles()
  return (
    <MenuItem className={classes.menuItem} {...props}>
      <span>{text}</span>
      {external && <OpenInNewOutlinedIcon className={classes.externalIcon} />}
    </MenuItem>
  )
}
